/* @jsx h */
import { defineComponent, h } from "vue";
import { WALLET_BINDINGS } from "@lemmonhq/blogic/wallet-design";
import type { WalletContentField, WalletPassKind } from "../../types";

/** Shared layout primitives for the designer panels. */

export const PanelSection = defineComponent<{
  title: string;
  description?: string;
}>({
  name: "PanelSection",
  props: ["title", "description"],
  setup(props, { slots }) {
    return () => (
      <section class="border border-base-content/10 rounded-xl bg-base-100 overflow-hidden">
        <header class="px-4 py-3 border-b border-base-content/10 bg-base-200/40">
          <h3 class="font-semibold text-sm">{props.title}</h3>
          {props.description && (
            <p class="text-xs text-base-content/60 mt-0.5">
              {props.description}
            </p>
          )}
        </header>
        <div class="p-4 flex flex-col gap-4">
          {slots.default && slots.default()}
        </div>
      </section>
    );
  },
});

export const PanelRow = defineComponent<{ label: string; hint?: string }>({
  name: "PanelRow",
  props: ["label", "hint"],
  setup(props, { slots }) {
    return () => (
      <label class="flex flex-col gap-1.5">
        <span class="text-xs font-medium text-base-content/80">
          {props.label}
        </span>
        {slots.default && slots.default()}
        {props.hint && (
          <span class="text-[11px] text-base-content/50">{props.hint}</span>
        )}
      </label>
    );
  },
});

export const ToggleRow = defineComponent<{
  label: string;
  hint?: string;
  value: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}>({
  name: "ToggleRow",
  props: ["label", "hint", "value", "disabled", "onChange"],
  setup(props) {
    return () => (
      <label
        class={`flex items-start justify-between gap-4 ${
          props.disabled ? "opacity-50" : "cursor-pointer"
        }`}
      >
        <span class="flex flex-col gap-0.5">
          <span class="text-xs font-medium text-base-content/80">
            {props.label}
          </span>
          {props.hint && (
            <span class="text-[11px] text-base-content/50">{props.hint}</span>
          )}
        </span>
        <input
          type="checkbox"
          class="toggle toggle-sm toggle-primary shrink-0"
          checked={props.value}
          disabled={props.disabled}
          onChange={(e) =>
            props.onChange((e.target as HTMLInputElement).checked)
          }
        />
      </label>
    );
  },
});

/**
 * Colour field: a native swatch plus a hex input, since merchants routinely
 * paste a brand hex rather than hunting for it on a wheel.
 */
export const ColorField = defineComponent<{
  label: string;
  value: string;
  hint?: string;
  onChange: (v: string) => void;
}>({
  name: "ColorField",
  props: ["label", "value", "hint", "onChange"],
  setup(props) {
    return () => (
      <PanelRow label={props.label} hint={props.hint}>
        <div class="flex items-center gap-2">
          <input
            type="color"
            class="w-10 h-9 rounded-lg border border-base-content/10 bg-base-100 cursor-pointer p-0.5"
            value={props.value}
            onInput={(e) => props.onChange((e.target as HTMLInputElement).value)}
          />
          <input
            class="input input-bordered input-sm font-mono flex-1"
            value={props.value}
            onInput={(e) => props.onChange((e.target as HTMLInputElement).value)}
          />
        </div>
      </PanelRow>
    );
  },
});

export const EmptyHint = defineComponent<{ icon: string; text: string }>({
  name: "EmptyHint",
  props: ["icon", "text"],
  setup(props) {
    return () => (
      <div class="flex flex-col items-center gap-2 py-8 text-base-content/40">
        <span class="material-symbols-outlined text-3xl">{props.icon}</span>
        <p class="text-xs">{props.text}</p>
      </div>
    );
  },
});

/**
 * A binding template: free text plus an "Insert token" menu over the kind's
 * registry, so merchants never have to guess token names.
 */
export const TemplateInput = defineComponent<{
  kind: WalletPassKind;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}>({
  name: "TemplateInput",
  props: ["kind", "value", "placeholder", "onChange"],
  setup(props) {
    return () => (
      <div class="flex items-center gap-2">
        <select
          class="select select-bordered select-xs w-36 shrink-0"
          value=""
          onChange={(e) => {
            const select = e.target as HTMLSelectElement;
            if (select.value) props.onChange(`${props.value}{{${select.value}}}`);
            select.value = "";
          }}
        >
          <option value="">Insert token…</option>
          {WALLET_BINDINGS[props.kind].map((entry) => (
            <option key={entry.token} value={entry.token}>
              {entry.description}
            </option>
          ))}
        </select>
        <input
          class="input input-bordered input-xs flex-1 min-w-0 font-mono"
          placeholder={props.placeholder}
          value={props.value}
          onInput={(e) => props.onChange((e.target as HTMLInputElement).value)}
        />
      </div>
    );
  },
});

const fieldLabel = (fields: WalletContentField[], key: string) => {
  const field = fields.find((f) => f.key === key);
  return field ? `${field.label || field.key}` : `${key} (missing)`;
};

/** Picks one content field by key. */
export const FieldKeySelect = defineComponent<{
  fields: WalletContentField[];
  value: string | null;
  allowNone?: boolean;
  onChange: (key: string | null) => void;
}>({
  name: "FieldKeySelect",
  props: ["fields", "value", "allowNone", "onChange"],
  setup(props) {
    return () => (
      <select
        class="select select-bordered select-sm"
        value={props.value ?? ""}
        onChange={(e) =>
          props.onChange((e.target as HTMLSelectElement).value || null)
        }
      >
        {props.allowNone && <option value="">None</option>}
        {props.fields.map((field) => (
          <option key={field.key} value={field.key}>
            {field.label || field.key}
          </option>
        ))}
      </select>
    );
  },
});

/**
 * Places content fields, in order, on one platform surface. Fields are
 * defined once in Content; this only chooses which appear here.
 */
export const FieldKeyPicker = defineComponent<{
  fields: WalletContentField[];
  value: string[];
  max: number;
  onChange: (keys: string[]) => void;
}>({
  name: "FieldKeyPicker",
  props: ["fields", "value", "max", "onChange"],
  setup(props) {
    const move = (index: number, delta: number) => {
      const next = [...props.value];
      const [key] = next.splice(index, 1);
      next.splice(index + delta, 0, key);
      props.onChange(next);
    };
    return () => {
      const available = props.fields.filter(
        (field) => !props.value.includes(field.key),
      );
      return (
        <div class="flex flex-col gap-1.5">
          {props.value.length === 0 && (
            <span class="text-[11px] text-base-content/40">No fields</span>
          )}
          {props.value.map((key, index) => (
            <div
              key={key}
              class="flex items-center gap-1 rounded-md bg-base-200/60 px-2 py-1"
            >
              <span class="flex-1 truncate text-xs">
                {fieldLabel(props.fields, key)}
              </span>
              <button
                class="btn btn-ghost btn-xs px-1"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <span class="material-symbols-outlined text-sm">arrow_upward</span>
              </button>
              <button
                class="btn btn-ghost btn-xs px-1"
                disabled={index === props.value.length - 1}
                onClick={() => move(index, 1)}
              >
                <span class="material-symbols-outlined text-sm">
                  arrow_downward
                </span>
              </button>
              <button
                class="btn btn-ghost btn-xs px-1 text-error"
                onClick={() =>
                  props.onChange(props.value.filter((k) => k !== key))
                }
              >
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          ))}
          {props.value.length < props.max && available.length > 0 && (
            <select
              class="select select-bordered select-xs"
              value=""
              onChange={(e) => {
                const select = e.target as HTMLSelectElement;
                if (select.value) props.onChange([...props.value, select.value]);
                select.value = "";
              }}
            >
              <option value="">Add field…</option>
              {available.map((field) => (
                <option key={field.key} value={field.key}>
                  {field.label || field.key}
                </option>
              ))}
            </select>
          )}
        </div>
      );
    };
  },
});
